import { Link } from "react-router-dom";
import type { CardAttention } from "@/modules/products/lib/cardAttention";

type Props = {
  attention: CardAttention;
  /** Editor route — used when the fix lives in the editor (action="navigate"). */
  editHref: string;
  /** Inline visibility flip — used when action="toggleVisible". */
  onToggleVisibility: () => void;
  /** First-photo upload — used when action="addPhoto". */
  onAddPhoto?: (file: File) => void;
  /** Open the inline stock editor — used when action="editStock". */
  onEditStock?: () => void;
  /** Shown in place of the CTA while a photo upload is in flight. */
  uploadingLabel?: string;
  pending: boolean;
  /**
   * "card" → full-width tinted bar; "compact" → tighter inline pill (table);
   * "overlay" → solid dark scrim band for sitting on top of the product image
   * (desktop grid), legible over any photo.
   */
  variant?: "card" | "compact" | "overlay";
  className?: string;
};

/**
 * The "understands me" treatment: states the specific blocker keeping a
 * product from selling and offers a one-tap route to its fix. Renders as a
 * <Link> (editor fixes) or a <button> (inline visibility flip) so the whole
 * strip is the affordance. Shared by the grid card, table row and mobile row.
 */
export function ProductAttentionStrip({
  attention,
  editHref,
  onToggleVisibility,
  onAddPhoto,
  onEditStock,
  uploadingLabel,
  pending,
  variant = "card",
  className,
}: Props) {
  const isError = attention.tone === "error";
  const isOverlay = variant === "overlay";
  const palette = isOverlay
    ? {
        // Solid scrim → readable over any product photo, on a device with hover.
        background:
          "linear-gradient(to top, rgba(11,18,32,0.92), rgba(11,18,32,0.74))",
        color: "#ffffff",
      }
    : {
        background: isError ? "var(--error-50)" : "var(--warning-100)",
        color: isError ? "var(--error-500)" : "var(--warning-500)",
      };

  const sizing = isOverlay
    ? "gap-2 px-3 py-2 text-[12.5px]"
    : variant === "compact"
      ? "gap-1.5 rounded-lg px-2 py-1 text-[12px]"
      : "gap-2 px-3.5 py-2.5 text-[13px]";

  // On the dark scrim the CTA needs its own bright tone.
  const ctaStyle = isOverlay
    ? { color: isError ? "#fca5a5" : "#fcd34d" }
    : undefined;

  // Action-only: the status badge already states what's wrong, so the banner
  // is just the fix (e.g. "Make visible", "Restock", "Add photo") with a hint
  // icon — no restating "not visible to customers". On the grid overlay it's a
  // full-width button (label centred); the compact list pill hugs its content.
  const content = (
    <span
      className={`flex min-w-0 items-center gap-1.5 font-semibold underline-offset-2 hover:underline ${
        isOverlay ? "w-full justify-center" : ""
      }`}
      style={ctaStyle}
    >
      <ActionIcon action={attention.action} />
      <span className="truncate">{attention.ctaLabel}</span>
    </span>
  );

  const justify = isOverlay ? "justify-center" : "justify-start";
  const sharedClass = `flex w-full items-center ${justify} no-underline transition-colors disabled:opacity-60 ${sizing} ${className ?? ""}`;

  if (attention.action === "addPhoto") {
    return (
      <label
        className={`cursor-pointer ${pending ? "opacity-70" : ""} ${sharedClass}`}
        style={palette}
        aria-disabled={pending || undefined}
      >
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="sr-only"
          disabled={pending}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) onAddPhoto?.(file);
          }}
        />
        {pending ? (
          <span className="min-w-0 truncate font-medium leading-snug">
            {uploadingLabel ?? attention.message}
          </span>
        ) : (
          content
        )}
      </label>
    );
  }

  if (attention.action === "editStock") {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onEditStock?.();
        }}
        className={`text-start ${sharedClass}`}
        style={palette}
      >
        {content}
      </button>
    );
  }

  if (attention.action === "toggleVisible") {
    return (
      <button
        type="button"
        disabled={pending}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleVisibility();
        }}
        className={`text-start ${sharedClass}`}
        style={palette}
      >
        {content}
      </button>
    );
  }

  return (
    <Link to={editHref} className={sharedClass} style={palette}>
      {content}
    </Link>
  );
}

function ActionIcon({ action }: { action: CardAttention["action"] }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    className: "shrink-0",
  };
  if (action === "addPhoto") {
    return (
      <svg {...common}>
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
      </svg>
    );
  }
  if (action === "toggleVisible") {
    return (
      <svg {...common}>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }
  if (action === "navigate") {
    return (
      <svg {...common}>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    );
  }
  // editStock — restock
  return (
    <svg {...common}>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}
